import React from 'react';

import {
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    ReactHookFormIntlNumberInput,
    ReactHookFormMultiSelect,
} from 'common/components/form';

const getLabel = (id) => {
    switch (id) {
        case 0:
            return 'Solar panel';
        case 1:
            return 'Inverter';
        case 2:
            return 'Storage';
        case 3:
            return 'Accessory';
        case 4:
            return 'Structure';
        case 5:
            return 'Workforce';
        case 6:
            return 'Additional';
        default:
            return 'Unknown';
    }
};

const RoiFields = ({
    control,
    disabled,
    incentiveOfferComplementsData = [],
    isFetching,
    name,
    setValue,
}) => {
    const { t } = useTranslation();
    const [isrSimulationType] = useWatch({
        control,
        name: [`alerts.${name}.sim_unit`],
    });

    const isAnnual = isrSimulationType === 'years';

    const options =
        incentiveOfferComplementsData?.map((item) => ({
            label: t(getLabel(item.element_type), { count: 2 }),
            value: item.id,
        })) || [];

    return (
        <Grid container>
            <Grid size={{ md: 'grow', xs: 18 }}>
                <ReactHookFormIntlNumberInput
                    append="%"
                    control={control}
                    disabled={disabled}
                    fullWidth
                    label={t('Percentage')}
                    min={0}
                    max={100}
                    name={`alerts.${name}.value_100`}
                    onBlur={({ value }) => {
                        setValue(`alerts.${name}.value`, value / 100);
                    }}
                />
            </Grid>

            <Grid size={{ md: 'grow', xs: 18 }}>
                <ReactHookFormIntlNumberInput
                    append={
                        isAnnual
                            ? t('Year', { count: 2 })
                            : t('Month', { count: 2 })
                    }
                    control={control}
                    disabled={disabled}
                    fullWidth
                    label={t('Simulation time')}
                    min={1}
                    max={isAnnual ? 50 : 600}
                    name={`alerts.${name}.sim_time`}
                    step="any"
                />
            </Grid>

            <Grid size={{ md: 'grow', xs: 18 }}>
                <ToggleButtonGroup
                    color="primary"
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(e) => {
                        setValue(`alerts.${name}.sim_unit`, e.target.value);
                        setValue(`alerts.${name}.sim_time`, '');
                    }}
                    value={isrSimulationType}
                    size="small"
                >
                    <ToggleButton value="months">{t('Monthly')}</ToggleButton>
                    <ToggleButton value="years">{t('Yearly')}</ToggleButton>
                </ToggleButtonGroup>
            </Grid>

            {options.length > 0 && (
                <Grid size={{ xs: 18 }}>
                    <ReactHookFormMultiSelect
                        allTextValue={t('All text', { count: 2 })}
                        control={control}
                        disabled={disabled || isFetching}
                        label={t('Apply to')}
                        name={`alerts.${name}.applicable_products`}
                        options={options}
                    />
                </Grid>
            )}
        </Grid>
    );
};

RoiFields.propTypes = {
    control: PropTypes.object,
    disabled: PropTypes.bool,
    incentiveOfferComplementsData: PropTypes.array,
    isFetching: PropTypes.bool,
    name: PropTypes.string,
    setValue: PropTypes.func,
};

export default RoiFields;
